import { memo } from 'react'
import { GalleryImageItem } from '@/components/commons/gallery-image-item'
import { ButtonLegacy } from '@/components/ui/button-legacy'
import {
	Expand01Icon,
	ThumbsDownIcon,
	ThumbsUpIcon
} from '@/components/ui/icons'
import type { ExperienceGalleryImage } from '@/features/experience-shell/types.ts'
import { useAddExperienceGalleryImageFlags } from '@/features/experience-vetting/queries/use-add-experience-gallery-image-flags'
import { useRemoveExperienceGalleryImageFlags } from '@/features/experience-vetting/queries/use-remove-experience-gallery-image-flags'
import { useUpdateExperienceVettingImageIsApproved } from '@/features/experience-vetting/queries/use-update-experience-vetting-image-is-approved.ts'
import {
	OpsVettingGalleryItemsContainer,
	OpsVettingImageTitle
} from '@/features/ops-vetting/components/ops-vetting-atoms'
import { useTranslation } from '@/translations/i18n'
import { ExperienceVettingCriteriaFlags } from './experience-vetting-criteria-flags'

export const ExperienceVettingCriteriaRejectedImageItem = memo(
	({
		image,
		hideAction,
		experienceId,
		galleryId,
		onClickExpand
	}: {
		image: ExperienceGalleryImage
		hideAction?: boolean
		experienceId: number
		galleryId: number
		onClickExpand?: () => void
	}) => {
		const { t } = useTranslation()

		const updateExperienceVettingImageIsApproved =
			useUpdateExperienceVettingImageIsApproved({
				experienceId,
				galleryId,
				imageId: image.id
			})

		const addExperienceGalleryImageFlags =
			useAddExperienceGalleryImageFlags({
				experienceId,
				sectionId: galleryId
			})
		const removeExperienceGalleryImageFlags =
			useRemoveExperienceGalleryImageFlags({
				experienceId,
				sectionId: galleryId
			})

		return (
			<OpsVettingGalleryItemsContainer direction='column' gap={4}>
				<GalleryImageItem
					bottomRightSlot={
						!hideAction && (
							<ButtonLegacy
								className='show-on-hover'
								onClick={onClickExpand}
								shape='square'
								size='sm'
								type='button'
								variant='secondary'
							>
								<Expand01Icon />
							</ButtonLegacy>
						)
					}
					src={image.thumbnails?.xs || image.url}
				/>
				<div className='flex w-full flex-col gap-2'>
					<OpsVettingImageTitle>{image.name}</OpsVettingImageTitle>
					<div className='flex w-full gap-3'>
						<ButtonLegacy
							className='w-full'
							disabled={hideAction}
							onClick={() =>
								updateExperienceVettingImageIsApproved.mutate({
									isApproved: true
								})
							}
							size={'sm'}
							type='button'
							variant={
								image.isApproved === true
									? 'primarySuccess'
									: 'secondary'
							}
						>
							<ThumbsUpIcon />
							{t('vetting:criteria.service.gallery.accept')}
						</ButtonLegacy>
						<ButtonLegacy
							className='w-full'
							disabled={hideAction}
							onClick={() =>
								updateExperienceVettingImageIsApproved.mutate({
									isApproved: false
								})
							}
							size={'sm'}
							type='button'
							variant={
								image.isApproved === false
									? 'primaryDanger'
									: 'secondary'
							}
						>
							<ThumbsDownIcon />
							{t('vetting:criteria.service.gallery.reject')}
						</ButtonLegacy>
					</div>
					{image.isApproved === false && (
						<ExperienceVettingCriteriaFlags
							addFlag={(flag) =>
								addExperienceGalleryImageFlags.mutate({
									flags: [flag],
									itemId: image.id
								})
							}
							flags={image.flags}
							hideAction={hideAction}
							removeFlag={(flag) =>
								removeExperienceGalleryImageFlags.mutate({
									flags: [flag],
									itemId: image.id
								})
							}
							scopes={['photo_gallery']}
						/>
					)}
				</div>
			</OpsVettingGalleryItemsContainer>
		)
	}
)
